import cx from "classnames";

import { Checkbox } from "atoms/checkbox/Checkbox";
import WarningTriangleIcon from "atoms/icon/icons/warning_triangle.svg";
import { Radio } from "atoms/radio/Radio";

interface ModalSelectorProps {
  /** Specify title */
  title: string;
  /** Specify subtext label of this selector (example: "recommended") */
  subtitle?: string;
  /** Specify label next to title */
  label: React.ReactNode | null;
  /** Specify tag prefix to title */
  tag: React.ReactNode | null;
  /** Specify description */
  description?: string | null;
  /** Specify default checked value */
  checked?: boolean;
  /** Specify onChange selector */
  onChange: (value: boolean) => void;
  /** Specify the type of selector, as default radio */
  type?: "radio" | "checkbox";
  disabled?: boolean;
  /** Specify if this is a variant modal */
  isVariantsModal?: boolean;
  /** Specify if this is a location type */
  isLocationType?: boolean;
  /** Specify warning icon to display */
  warningIcon?: React.ReactNode;
  /** Specify whether to show warning */
  showWarning?: boolean;
}

export const ModalSelector = ({
  title,
  subtitle,
  label,
  tag,
  description,
  checked,
  onChange,
  type = "radio",
  disabled = false,
  isLocationType = false,
  showWarning = false,
}: ModalSelectorProps) => {
  return (
    <div
      className={cx(
        isLocationType
          ? "mb-3 mt-1 rounded-xl border-2 border-gray-200 p-4 hover:border-brand-color"
          : "border-b border-background-two py-4",
        checked && isLocationType ? "border-brand-color" : ""
      )}
      aria-label="button"
      onClick={(e) => {
        if (!disabled) {
          onChange(!checked);
          e.stopPropagation();
        }
      }}
    >
      <div className="flex cursor-pointer items-center justify-between">
        <div className="content-right -ml-2.5 flex gap-5 text-right text-primary">
          {type === "checkbox" && (
            <Checkbox
              label={title}
              subtitle={subtitle}
              checked={checked}
              disabled={disabled}
              textStyle="text-bold2"
            />
          )}
          {type === "radio" && (
            <div className="flex items-center">
              <Radio checked={checked} onChange={onChange} name={title} />
              <div
                className={cx(
                  "flex flex-col text-left align-middle",
                  isLocationType ? "ml-3 w-full" : "ml-1.5 gap-2"
                )}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <div
                      className={cx(
                        "text-primary",
                        description ? "text-bold1" : "text-bold2"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {tag}
                        {title}
                      </div>
                    </div>
                  </div>
                  {label && (
                    <div className="text-body2 text-secondary">{label}</div>
                  )}
                  {subtitle && (
                    <div className="text-body2 text-secondary">{subtitle}</div>
                  )}
                </div>
                {description && (
                  <div className="content-right text-body2 text-secondary">
                    {isLocationType && showWarning ? (
                      <div className="flex items-center justify-between">
                        <div>{description}</div>
                        <div className="relative -top-6 ml-24">
                          <WarningTriangleIcon width="28" height="28" />
                        </div>
                      </div>
                    ) : (
                      description
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
